import React, { useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import UserAvatar from 'components/UI/UserAvatar';
import {
  useDeleteUserMePictureMutation,
  usePutUserMePictureMutation,
} from 'services/login/api';
import { useAuth } from 'services/login/hooks';
import { setCredentials } from 'services/login/slice';
import { fileToProfilePictureDataUrl } from 'utils/profilePicture';

const SettingsAccount = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useAuth();
  const { user } = auth;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const [putUserMePicture, { isLoading: isUploading }] = usePutUserMePictureMutation();
  const [deleteUserMePicture, { isLoading: isRemoving }] = useDeleteUserMePictureMutation();

  const isBusy = isUploading || isRemoving;

  const handleSelectPicture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setErrorMessage(null);
    try {
      const image = await fileToProfilePictureDataUrl(file);
      const result = await putUserMePicture({ image });
      if ('error' in result) {
        setErrorMessage(t('profilePictureUploadFailed'));
        return;
      }
      if (result.data && user) {
        dispatch(setCredentials({ user: { ...user, ...result.data } }));
      }
      setPreviewSrc(image);
    } catch (error) {
      if (error instanceof Error && error.message === 'INVALID_TYPE') {
        setErrorMessage(t('profilePictureInvalidType'));
        return;
      }
      setErrorMessage(t('profilePictureUploadFailed'));
    }
  };

  const handleRemovePicture = async () => {
    setErrorMessage(null);
    const result = await deleteUserMePicture();
    if ('error' in result) {
      setErrorMessage(t('profilePictureUploadFailed'));
      return;
    }
    if (result.data && user) {
      dispatch(setCredentials({ user: { ...user, ...result.data } }));
    }
    setPreviewSrc(null);
  };

  return (
    <div>
      <label className="form-label">{t('profilePicture')}</label>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: 16,
        }}
      >
        <UserAvatar
          alt={t('profilePicture')}
          hasProfilePicture={user?.hasProfilePicture}
          modifiedAt={user?.modifiedAt}
          previewSrc={previewSrc}
          size={72}
          userId={user?.id}
        />
        <div>
          <input
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleSelectPicture}
            ref={fileInputRef}
            style={{ display: 'none' }}
            type="file"
          />
          <Button
            disabled={isBusy}
            onClick={() => fileInputRef.current?.click()}
            variant="outline-secondary"
          >
            {t('changeProfilePicture')}
          </Button>
          {
            user?.hasProfilePicture && (
              <Button
                disabled={isBusy}
                onClick={handleRemovePicture}
                style={{ marginLeft: 8 }}
                variant="outline-danger"
              >
                {t('removeProfilePicture')}
              </Button>
            )
          }
        </div>
      </div>
      {
        errorMessage && (
          <Alert
            className="mt-3 mb-0"
            variant="danger"
          >
            {errorMessage}
          </Alert>
        )
      }
    </div>
  );
};

export default SettingsAccount;
